import React, { useEffect, useState } from "react";
import { withRouter, useHistory } from "react-router-dom";
import "./main.scss";
import { agmsClientRequestGet, agmsClientRequestPut, agmsClientRequestDelete, agmsClientRequestPost } from "../../apis/request";
import secureLocalStorage from "react-secure-storage";
import Loading from "../../assets/uxnicon/loadingIcon.png";
import AdminLayout from "./AdminLayout";
import { formatServerTime } from "../../utils/serverTime";

const PAGE_SIZE = 20;
const SEX_CODE_TYPE = 1600;      // 성별 코드 그룹
const SEX_UNKNOWN_CODE = 1603;   // 성별 '선택 안함' 코드 — 관리자 화면에선 '모름'으로 표시
const DIABETES_CODE_TYPE = 1700; // 당뇨유형 코드 그룹
// 권한 코드 (code_type_id=1000): 1001 관리자 / 1002 일반관리자 / 1003 일반유저
const ROLE_CODE = { ADMIN: 1001, SUB_ADMIN: 1002, USER: 1003 };
const ROLE_LABEL = { 1001: "최고 관리자", 1002: "관리자", 1003: "일반유저" };

const thBase = {
  backgroundColor: "#787397",
  color: "#fff",
  fontWeight: "bold",
  fontFamily: "Noto Sans CJK KR",
  padding: "10px 16px",
  borderRight: "1px solid #EBEBEB",
  textAlign: "center",
};

const tdBase = {
  backgroundColor: "#fff",
  textAlign: "center",
  borderRight: "1px solid #EBEBEB",
  borderBottom: "1px solid #EBEBEB",
  padding: "8px 12px",
};

// 페이지 버튼 스타일 (active면 보라 강조)
const pageBtn = (active, disabled) => ({
  minWidth: "34px",
  padding: "6px 10px",
  border: active ? "1px solid #787397" : "1px solid #d5d0dd",
  borderRadius: "6px",
  background: active ? "#787397" : "#fff",
  color: active ? "#fff" : "#5E2589",
  fontWeight: active ? "bold" : "normal",
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.4 : 1,
});

// 행 관리 버튼 (edit·device=보라, delete=빨강)
const rowBtn = (variant) => ({
  padding: "5px 12px",
  marginRight: variant === "delete" ? 0 : "6px",
  border: variant === "delete" ? "1px solid #d9534f" : "1px solid #787397",
  background: "#fff",
  color: variant === "delete" ? "#d9534f" : "#5E2589",
  borderRadius: "6px",
  fontSize: "13px",
  cursor: "pointer",
});

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalBox = {
  background: "#fff",
  borderRadius: "12px",
  padding: "32px",
  width: "560px",
  maxWidth: "94vw",
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
};

const fieldLabel = { display: "block", fontSize: "13px", color: "#84818A", marginBottom: "4px" };
const fieldInput = { width: "100%", padding: "8px 10px", border: "1px solid #CBCBCB", borderRadius: "8px", marginBottom: "14px", boxSizing: "border-box" };

const AdminUserList = () => {
  const history = useHistory();
  // 최고관리자(1001)만 관리(수정/계정/기기/삭제) 노출. 일반관리자(1002)는 조회만.
  const isSuperAdmin = Number(secureLocalStorage.getItem("role")) === ROLE_CODE.ADMIN;
  // 최고관리자 탭: 전체 유저 화면을 그대로 쓰고 목록 API·제목만 다름. 권한 필터 없음.
  const isAdminTab = history.location.pathname === "/admin/admin-tab";
  const [pageResp, setPageResp] = useState(null); // {content, totalElements, totalPages, page, size}
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");        // 적용된 검색어
  const [searchInput, setSearchInput] = useState(""); // 입력창 값
  const [roleFilter, setRoleFilter] = useState("");   // 권한별 조회 ("" 전체 / 1001 최고 관리자 / 1002 관리자 / 1003 일반유저)

  // 수정/삭제
  const [sexOptions, setSexOptions] = useState([]);
  const [diabetesOptions, setDiabetesOptions] = useState([]);
  const [editForm, setEditForm] = useState(null);  // 수정 중인 유저 폼 (null이면 모달 닫힘)
  const [deleteTarget, setDeleteTarget] = useState(null); // 삭제 확인 대상
  const [busy, setBusy] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(null); // hover 중인 행 (보라 하이라이트)

  // 기기 관리 (유저별 연결기기)
  const [deviceUser, setDeviceUser] = useState(null); // 기기 모달 대상 유저 (null=닫힘)
  const [deviceList, setDeviceList] = useState(null);  // 연결기기 목록 (null=로딩중)
  const [unlinkTarget, setUnlinkTarget] = useState(null); // 연결해제 확인 대상

  // 계정 관리 (비번 초기화 / 잠금 해제 / 만료 연장)
  const [accountUser, setAccountUser] = useState(null);   // 계정 모달 대상 유저 (null=닫힘)
  const [accountStatus, setAccountStatus] = useState(null); // {email, expire_at, expired, locked} (null=로딩중)
  const [newPassword, setNewPassword] = useState("");
  const [roleConfirm, setRoleConfirm] = useState(null); // 권한 변경 확인 대상 role ("ADMIN"|"USER")

  const callApi = async (p, s, r) => {
    setError(null);
    try {
      const roleParam = (r !== undefined && r !== null && r !== "") ? `&role_code=${r}` : "";
      const listPath = isAdminTab ? "/api/admin/admin-tab" : "/api/admin/users";
      const res = await agmsClientRequestGet(`${listPath}?page=${p}&size=${PAGE_SIZE}&search=${encodeURIComponent(s || "")}${roleParam}`);
      if (res && Array.isArray(res.content)) {
        setPageResp(res);
      } else {
        setPageResp({ content: [], totalElements: 0, totalPages: 1, page: p, size: PAGE_SIZE });
      }
    } catch (e) {
      setError("유저 목록을 불러오지 못했습니다. (권한 또는 서버 오류)");
      setPageResp({ content: [], totalElements: 0, totalPages: 1, page: p, size: PAGE_SIZE });
    }
  };

  // 페이지/검색어/권한필터 바뀔 때마다 서버에서 재조회
  useEffect(() => {
    callApi(page, search, roleFilter);
  }, [page, search, roleFilter]);

  // 코드 옵션(성별·당뇨유형) 최초 1회 로드
  useEffect(() => {
    (async () => {
      try {
        const [sex, diabetes] = await Promise.all([
          agmsClientRequestGet(`/api/admin/codes/${SEX_CODE_TYPE}`),
          agmsClientRequestGet(`/api/admin/codes/${DIABETES_CODE_TYPE}`),
        ]);
        setSexOptions(Array.isArray(sex) ? sex : []);
        setDiabetesOptions(Array.isArray(diabetes) ? diabetes : []);
      } catch (e) {
        // 옵션 로드 실패해도 나머지 폼은 동작
      }
    })();
  }, []);

  // 검색 실행: 1페이지로 리셋하고 검색어 적용
  const applySearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  // 수정 모달 열기 (행 클릭=상세로 가지 않게 stopPropagation)
  const openEdit = (e, u) => {
    e.stopPropagation();
    setEditForm({
      id: u.id,
      name: u.name ?? "",
      sex: u.sex ?? "",
      age: u.age ?? "",
      height: u.height ?? "",
      weight: u.weight ?? "",
      diabetes_type: u.diabetes_type ?? "",
      target_glucose_low: u.target_glucose_low ?? "",
      target_glucose_high: u.target_glucose_high ?? "",
    });
  };

  const setField = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));

  // 숫자 문자열 → 정수(빈값은 null)
  const toIntOrNull = (v) => (v === "" || v === null || v === undefined ? null : parseInt(v, 10));

  const submitEdit = async () => {
    if (!editForm) return;
    setBusy(true);
    try {
      const body = {
        name: editForm.name,
        sex: toIntOrNull(editForm.sex),
        age: toIntOrNull(editForm.age),
        height: toIntOrNull(editForm.height),
        weight: toIntOrNull(editForm.weight),
        diabetes_type: toIntOrNull(editForm.diabetes_type),
        target_glucose_low: toIntOrNull(editForm.target_glucose_low),
        target_glucose_high: toIntOrNull(editForm.target_glucose_high),
      };
      await agmsClientRequestPut(`/api/admin/users/${editForm.id}`, body);
      setEditForm(null);
      await callApi(page, search, roleFilter);
    } catch (e) {
      alert("수정에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  // 기기 모달 열기 + 유저 연결기기 조회
  const openDevices = async (e, u) => {
    e.stopPropagation();
    setDeviceUser(u);
    setDeviceList(null);
    try {
      const res = await agmsClientRequestGet(`/api/admin/users/${u.id}/devices`);
      setDeviceList(Array.isArray(res) ? res : []);
    } catch (err) {
      setDeviceList([]);
    }
  };

  const confirmUnlink = async () => {
    if (!unlinkTarget || !deviceUser) return;
    setBusy(true);
    try {
      // 연결해제 = 종료 처리(is_finish=true + 종료일). 삭제 아님, 측정 기록 유지.
      await agmsClientRequestPut(`/api/admin/user-devices/${unlinkTarget.user_device_id}/finish`);
      setUnlinkTarget(null);
      // 목록 갱신
      const res = await agmsClientRequestGet(`/api/admin/users/${deviceUser.id}/devices`);
      setDeviceList(Array.isArray(res) ? res : []);
    } catch (err) {
      alert("종료 처리에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  // 계정 모달 열기 + 상태 조회
  const openAccount = async (e, u) => {
    e.stopPropagation();
    setAccountUser(u);
    setAccountStatus(null);
    setNewPassword("");
    try {
      const res = await agmsClientRequestGet(`/api/admin/users/${u.id}/account`);
      setAccountStatus(res || {});
    } catch (err) {
      setAccountStatus({});
    }
  };

  const refetchAccount = async () => {
    if (!accountUser) return;
    try {
      const res = await agmsClientRequestGet(`/api/admin/users/${accountUser.id}/account`);
      setAccountStatus(res || {});
    } catch (err) { /* 무시 */ }
  };

  const submitPassword = async () => {
    if (!accountUser) return;
    const pw = newPassword.trim();
    if (pw.length < 4) { alert("비밀번호는 4자 이상 입력하세요."); return; }
    setBusy(true);
    try {
      await agmsClientRequestPut(`/api/admin/users/${accountUser.id}/password`, { password: pw });
      setNewPassword("");
      await refetchAccount(); // 비번 초기화 시 잠금도 풀림
      alert("비밀번호를 초기화했습니다.");
    } catch (err) {
      alert("비밀번호 초기화에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const doUnlock = async () => {
    if (!accountUser) return;
    setBusy(true);
    try {
      await agmsClientRequestPost(`/api/admin/users/${accountUser.id}/unlock`, {});
      await refetchAccount();
    } catch (err) {
      alert("잠금 해제에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const confirmRoleChange = async () => {
    if (!accountUser || !roleConfirm) return;
    setBusy(true);
    try {
      await agmsClientRequestPut(`/api/admin/users/${accountUser.id}/role`, { roleCode: roleConfirm });
      // 모달의 현재 유저 권한 코드/이름 갱신 (roleConfirm = 코드 1001/1002/1003)
      setAccountUser((u) => ({ ...u, role_code: roleConfirm, role_name: ROLE_LABEL[roleConfirm] }));
      setRoleConfirm(null);
      await callApi(page, search, roleFilter);
    } catch (err) {
      alert("권한 변경에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const doRenewExpire = async () => {
    if (!accountUser) return;
    setBusy(true);
    try {
      await agmsClientRequestPost(`/api/admin/users/${accountUser.id}/renew-expire`, {});
      await refetchAccount();
    } catch (err) {
      alert("만료 연장에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await agmsClientRequestDelete(`/api/admin/users/${deleteTarget.id}`);
      setDeleteTarget(null);
      // 마지막 페이지의 마지막 항목을 지운 경우 이전 페이지로
      const remaining = (pageResp?.content?.length ?? 1) - 1;
      const nextPage = remaining === 0 && page > 1 ? page - 1 : page;
      if (nextPage !== page) {
        setPage(nextPage);
      } else {
        await callApi(page, search, roleFilter);
      }
    } catch (e) {
      alert("삭제에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  // 최초 로딩(응답 아직 없음)일 때만 스피너
  if (pageResp === null) {
    return (
      <AdminLayout>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "300px" }}>
          <img
            src={Loading}
            alt="loading..."
            style={{ width: "50px", height: "50px", animation: ".75s linear infinite spinner-border" }}
          />
        </div>
      </AdminLayout>
    );
  }

  const rawContent = pageResp.content ?? [];
  // 일반관리자(SUB_ADMIN)는 목록에서 자기 계정 숨김 — 확증임상 요구(8/25 회의)
  const myUserId = Number(secureLocalStorage.getItem("authUser"));
  const content = isSuperAdmin ? rawContent : rawContent.filter((u) => Number(u.id) !== myUserId);
  const total = pageResp.totalElements ?? 0;
  const totalPages = Math.max(1, pageResp.totalPages ?? 1);
  const currentPage = Math.min(page, totalPages);

  return (
    <AdminLayout>
      <div style={{ width: "100%" }}>
        <div
          style={{
            marginBottom: "17px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontWeight: "bold", fontSize: "20px" }}>{isAdminTab ? "최고관리자" : "전체 유저 목록"}</div>
          <div style={{ color: "#84818A" }}>총 {total}명</div>
        </div>

        {/* 검색 (이름·이메일, 서버 검색) */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "14px" }}>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
            placeholder="이름 또는 이메일 검색"
            style={{ flex: "0 0 320px", maxWidth: "100%", padding: "8px 12px", border: "1px solid #CBCBCB", borderRadius: "8px" }}
          />
          <button
            onClick={applySearch}
            style={{ padding: "8px 16px", background: "#787397", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer" }}
          >
            검색
          </button>
          {search && (
            <button
              onClick={() => { setSearchInput(""); setSearch(""); setPage(1); }}
              style={{ padding: "8px 14px", background: "#fff", color: "#5E2589", border: "1px solid #d5d0dd", borderRadius: "8px", cursor: "pointer" }}
            >
              초기화
            </button>
          )}
          {/* 권한별 조회 드롭다운 — 전체 / 최고 관리자 / 관리자 / 일반유저 */}
          {!isAdminTab && (
            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
              style={{ marginLeft: "auto", padding: "8px 12px", border: "1px solid #CBCBCB", borderRadius: "8px", cursor: "pointer", background: "#fff" }}
            >
              <option value="">전체 권한</option>
              <option value={ROLE_CODE.ADMIN}>최고 관리자</option>
              <option value={ROLE_CODE.SUB_ADMIN}>관리자</option>
              <option value={ROLE_CODE.USER}>일반유저</option>
            </select>
          )}
        </div>

        {error && (
          <div style={{ color: "#d9534f", marginBottom: "12px" }}>{error}</div>
        )}

        <div style={{ overflowX: "auto" }}>
          <table style={{ minWidth: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...thBase, borderRadius: "5px 0px 0px 5px", minWidth: "60px" }}>ID</th>
                <th style={{ ...thBase, minWidth: "90px" }}>이름</th>
                <th style={{ ...thBase, minWidth: "220px" }}>이메일</th>
                {isSuperAdmin && (
                  <th style={{ ...thBase, minWidth: "80px" }}>권한</th>
                )}
                <th style={{ ...thBase, minWidth: "60px" }}>성별</th>
                <th style={{ ...thBase, minWidth: "60px" }}>나이</th>
                <th style={{ ...thBase, minWidth: "80px" }}>당뇨유형</th>
                <th style={isSuperAdmin
                  ? { ...thBase, minWidth: "120px" }
                  : { ...thBase, borderRight: "none", borderRadius: "0px 5px 5px 0px", minWidth: "120px" }}>목표혈당</th>
                {isSuperAdmin && (
                  <th style={{ ...thBase, borderRight: "none", borderRadius: "0px 5px 5px 0px", minWidth: "290px" }}>관리</th>
                )}
              </tr>
            </thead>
            <tbody>
              {content.length === 0 ? (
                <tr>
                  <td style={{ ...tdBase, borderRight: "none" }} colSpan={isSuperAdmin ? 9 : 7}>
                    표시할 유저가 없습니다.
                  </td>
                </tr>
              ) : (
                content.map((u, i) => {
                  const cellBg = hoverIdx === i ? "#EFE9F7" : "#fff"; // hover 시 연보라
                  const cell = { ...tdBase, backgroundColor: cellBg };
                  const cellLast = { ...tdBase, backgroundColor: cellBg, borderRight: "none" };
                  return (
                  <tr
                    key={u.id ?? i}
                    onClick={() => history.push(`/admin/users/${u.id}`, { user: u })}
                    onMouseEnter={() => setHoverIdx(i)}
                    onMouseLeave={() => setHoverIdx(null)}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={cell}>{u.id}</td>
                    <td style={cell}>{u.name}</td>
                    <td style={cell}>{u.email}</td>
                    {isSuperAdmin && <td style={cell}>{u.role_name ?? "-"}</td>}
                    <td style={cell}>{u.sex === SEX_UNKNOWN_CODE ? "모름" : (u.sex_name ?? "-")}</td>
                    <td style={cell}>{u.age ?? "-"}</td>
                    <td style={cell}>{u.diabetes_type_name ?? "-"}</td>
                    <td style={isSuperAdmin ? cell : cellLast}>
                      {u.target_glucose_low ?? "-"} ~ {u.target_glucose_high ?? "-"}
                    </td>
                    {isSuperAdmin && (
                      <td style={cellLast}>
                        <button style={rowBtn("edit")} onClick={(e) => openEdit(e, u)}>수정</button>
                        <button style={rowBtn("account")} onClick={(e) => openAccount(e, u)}>계정</button>
                        <button style={rowBtn("device")} onClick={(e) => openDevices(e, u)}>기기</button>
                        <button style={rowBtn("delete")} onClick={(e) => { e.stopPropagation(); setDeleteTarget(u); }}>삭제</button>
                      </td>
                    )}
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 (서버 페이징) — 1페이지여도 항상 표시 */}
        {content.length > 0 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "6px", marginTop: "20px" }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={pageBtn(false, currentPage === 1)}
            >
              이전
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)} style={pageBtn(p === currentPage, false)}>
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={pageBtn(false, currentPage === totalPages)}
            >
              다음
            </button>
          </div>
        )}
      </div>

      {/* 수정 모달 */}
      {editForm && (
        <div style={modalOverlay}>
          <div style={modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: "bold", fontSize: "18px", marginBottom: "18px" }}>유저 수정</div>

            <label style={fieldLabel}>이름</label>
            <input style={fieldInput} value={editForm.name} onChange={(e) => setField("name", e.target.value)} />

            <label style={fieldLabel}>성별</label>
            <select style={fieldInput} value={editForm.sex} onChange={(e) => setField("sex", e.target.value)}>
              {/* 빈 값(null) 저장은 실패해서 뺌 — 모를 때는 코드 1603('선택 안함')을 '모름'으로 보여줌 */}
              {editForm.sex === "" && <option value="" disabled>성별 선택</option>}
              {sexOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.id === SEX_UNKNOWN_CODE ? "모름" : o.name}</option>
              ))}
            </select>

            <label style={fieldLabel}>나이</label>
            <input style={fieldInput} type="number" value={editForm.age} onChange={(e) => setField("age", e.target.value)} />

            <label style={fieldLabel}>키 (cm)</label>
            <input style={fieldInput} type="number" value={editForm.height} onChange={(e) => setField("height", e.target.value)} />

            <label style={fieldLabel}>몸무게 (kg)</label>
            <input style={fieldInput} type="number" value={editForm.weight} onChange={(e) => setField("weight", e.target.value)} />

            <label style={fieldLabel}>당뇨유형</label>
            <select style={fieldInput} value={editForm.diabetes_type} onChange={(e) => setField("diabetes_type", e.target.value)}>
              <option value="">선택 안 함</option>
              {diabetesOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>

            <div style={{ display: "flex", gap: "10px" }}>
              <div style={{ flex: 1 }}>
                <label style={fieldLabel}>목표혈당(하한)</label>
                <input style={fieldInput} type="number" value={editForm.target_glucose_low} onChange={(e) => setField("target_glucose_low", e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={fieldLabel}>목표혈당(상한)</label>
                <input style={fieldInput} type="number" value={editForm.target_glucose_high} onChange={(e) => setField("target_glucose_high", e.target.value)} />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "6px" }}>
              <button
                onClick={() => setEditForm(null)}
                disabled={busy}
                style={{ padding: "9px 18px", background: "#fff", color: "#5E2589", border: "1px solid #d5d0dd", borderRadius: "8px", cursor: "pointer" }}
              >
                취소
              </button>
              <button
                onClick={submitEdit}
                disabled={busy}
                style={{ padding: "9px 18px", background: "#787397", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer" }}
              >
                {busy ? "저장 중..." : "저장"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {deleteTarget && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, width: "360px" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: "bold", fontSize: "18px", marginBottom: "12px" }}>유저 삭제</div>
            <div style={{ color: "#555", lineHeight: 1.6, marginBottom: "20px" }}>
              <b>{deleteTarget.name}</b> ({deleteTarget.email}) 유저를 삭제할까요?<br />
              삭제하면 목록에서 사라지고 로그인도 차단됩니다.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={busy}
                style={{ padding: "9px 18px", background: "#fff", color: "#5E2589", border: "1px solid #d5d0dd", borderRadius: "8px", cursor: "pointer" }}
              >
                취소
              </button>
              <button
                onClick={confirmDelete}
                disabled={busy}
                style={{ padding: "9px 18px", background: "#d9534f", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer" }}
              >
                {busy ? "삭제 중..." : "삭제"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 연결 기기 모달 */}
      {deviceUser && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, width: "720px" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: "bold", fontSize: "18px", marginBottom: "4px" }}>연결 기기</div>
            <div style={{ color: "#84818A", fontSize: "13px", marginBottom: "16px" }}>
              {deviceUser.name} ({deviceUser.email})
            </div>

            {deviceList === null ? (
              <div style={{ padding: "30px 0", textAlign: "center", color: "#84818A" }}>불러오는 중...</div>
            ) : deviceList.length === 0 ? (
              <div style={{ padding: "30px 0", textAlign: "center", color: "#84818A" }}>연결된 기기가 없습니다.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ minWidth: "100%" }}>
                  <thead>
                    <tr>
                      <th style={{ ...thBase, borderRadius: "5px 0 0 5px", minWidth: "100px" }}>시리얼</th>
                      <th style={{ ...thBase, minWidth: "150px" }}>시작일</th>
                      <th style={{ ...thBase, minWidth: "150px" }}>종료일</th>
                      <th style={{ ...thBase, minWidth: "70px" }}>상태</th>
                      <th style={{ ...thBase, borderRight: "none", borderRadius: "0 5px 5px 0", minWidth: "90px" }}>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deviceList.map((d) => (
                      <tr key={d.user_device_id}>
                        <td style={tdBase}>{d.serial_number ?? "-"}</td>
                        <td style={tdBase}>{formatServerTime(d.started_dt, "YYYY-MM-DD HH:mm:ss", "-")}</td>
                        <td style={tdBase}>{d.is_finish ? formatServerTime(d.last_dt, "YYYY-MM-DD HH:mm:ss", "-") : "-"}</td>
                        <td style={tdBase}>{d.is_finish ? "종료" : "활성"}</td>
                        <td style={{ ...tdBase, borderRight: "none" }}>
                          {d.is_finish
                            ? <span style={{ color: "#aaa" }}>-</span>
                            : <button
                                disabled
                                title="앱 연동 후 지원 예정 — 지금은 종료해도 앱이 계속 수집합니다"
                                style={{ ...rowBtn("delete"), background: "#ccc", borderColor: "#ccc", color: "#888", cursor: "not-allowed" }}
                              >종료</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "18px" }}>
              <button
                onClick={() => setDeviceUser(null)}
                disabled={busy}
                style={{ padding: "9px 18px", background: "#fff", color: "#5E2589", border: "1px solid #d5d0dd", borderRadius: "8px", cursor: "pointer" }}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 측정 종료 확인 모달 */}
      {unlinkTarget && (
        <div style={{ ...modalOverlay, zIndex: 1100 }}>
          <div style={{ ...modalBox, width: "360px" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: "bold", fontSize: "18px", marginBottom: "12px" }}>측정 종료</div>
            <div style={{ color: "#555", lineHeight: 1.6, marginBottom: "20px" }}>
              기기 <b>{unlinkTarget.serial_number}</b> 측정을 종료할까요?<br />
              종료일이 기록되고, 측정 기록·데이터는 그대로 유지됩니다.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button
                onClick={() => setUnlinkTarget(null)}
                disabled={busy}
                style={{ padding: "9px 18px", background: "#fff", color: "#5E2589", border: "1px solid #d5d0dd", borderRadius: "8px", cursor: "pointer" }}
              >
                취소
              </button>
              <button
                onClick={confirmUnlink}
                disabled={busy}
                style={{ padding: "9px 18px", background: "#d9534f", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer" }}
              >
                {busy ? "종료 중..." : "측정 종료"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 계정 관리 모달 (비번 초기화 / 잠금 해제 / 만료 연장) */}
      {accountUser && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, width: "440px" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: "bold", fontSize: "18px", marginBottom: "4px" }}>계정 관리</div>
            <div style={{ color: "#84818A", fontSize: "13px", marginBottom: "18px" }}>
              {accountUser.name} ({accountUser.email})
            </div>

            {accountStatus === null ? (
              <div style={{ padding: "20px 0", textAlign: "center", color: "#84818A" }}>불러오는 중...</div>
            ) : (
              <>
                {/* 상태 */}
                <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
                  <div style={{ flex: 1, padding: "10px 12px", borderRadius: "8px", background: "#F6F4FA" }}>
                    <div style={{ fontSize: "12px", color: "#84818A" }}>만료일</div>
                    <div style={{ fontWeight: "bold", color: accountStatus.expired ? "#d9534f" : "#2f7a41" }}>
                      {accountStatus.expire_at ?? "미설정"} {accountStatus.expired ? "(만료됨)" : ""}
                    </div>
                  </div>
                  <div style={{ flex: 1, padding: "10px 12px", borderRadius: "8px", background: "#F6F4FA" }}>
                    <div style={{ fontSize: "12px", color: "#84818A" }}>로그인 잠금</div>
                    <div style={{ fontWeight: "bold", color: accountStatus.locked ? "#d9534f" : "#2f7a41" }}>
                      {accountStatus.locked ? "잠김 (5회 실패)" : "정상"}
                    </div>
                  </div>
                </div>

                {/* 비밀번호 초기화 */}
                <div style={{ marginBottom: "18px" }}>
                  <label style={fieldLabel}>비밀번호 초기화</label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="새 비밀번호 (4자 이상)"
                      style={{ flex: 1, padding: "8px 10px", border: "1px solid #CBCBCB", borderRadius: "8px", boxSizing: "border-box" }}
                    />
                    <button
                      onClick={submitPassword}
                      disabled={busy}
                      style={{ padding: "8px 14px", background: "#787397", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      초기화
                    </button>
                  </div>
                  <div style={{ fontSize: "12px", color: "#84818A", marginTop: "4px" }}>초기화하면 잠금도 함께 풀립니다.</div>
                </div>

                {/* 잠금 해제 / 만료 연장 */}
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={doUnlock}
                    disabled={busy || !accountStatus.locked}
                    style={{ flex: 1, padding: "9px 0", background: accountStatus.locked ? "#fff" : "#f3f3f3", color: accountStatus.locked ? "#5E2589" : "#aaa", border: "1px solid #d5d0dd", borderRadius: "8px", cursor: accountStatus.locked ? "pointer" : "default" }}
                  >
                    잠금 해제
                  </button>
                  <button
                    onClick={doRenewExpire}
                    disabled={busy}
                    style={{ flex: 1, padding: "9px 0", background: "#fff", color: "#5E2589", border: "1px solid #d5d0dd", borderRadius: "8px", cursor: "pointer" }}
                  >
                    만료 연장 (+6개월)
                  </button>
                </div>

                {/* 권한 변경 (3단계: 일반유저 / 관리자 / 최고 관리자) */}
                <div style={{ marginTop: "20px", paddingTop: "18px", borderTop: "1px solid #EEE" }}>
                  <label style={fieldLabel}>권한</label>
                  {(() => {
                    const cur = accountUser.role_code ?? ROLE_CODE.USER;   // 권한 코드
                    const isSelf = String(accountUser.id) === String(secureLocalStorage.getItem("authUser"));
                    const tiers = [
                      { key: ROLE_CODE.USER, label: "일반유저" },
                      { key: ROLE_CODE.SUB_ADMIN, label: "관리자" },
                      { key: ROLE_CODE.ADMIN, label: "최고 관리자" },
                    ];
                    return (
                      <div style={{ display: "flex", gap: "8px" }}>
                        {tiers.map((t) => {
                          const active = cur === t.key;
                          const locked = busy || isSelf || active;
                          return (
                            <button
                              key={t.key}
                              onClick={() => !active && !isSelf && setRoleConfirm(t.key)}
                              disabled={locked}
                              style={{
                                flex: 1, padding: "9px 0", borderRadius: "8px",
                                background: active ? "#787397" : "#fff",
                                color: active ? "#fff" : (isSelf ? "#aaa" : "#5E2589"),
                                border: active ? "1px solid #787397" : "1px solid #d5d0dd",
                                fontWeight: active ? "bold" : "normal",
                                cursor: locked ? "default" : "pointer",
                                opacity: (isSelf && !active) ? 0.5 : 1,
                              }}
                            >
                              {t.label}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {String(accountUser.id) === String(secureLocalStorage.getItem("authUser")) && (
                    <div style={{ fontSize: "12px", color: "#84818A", marginTop: "6px" }}>본인 계정의 권한은 변경할 수 없습니다.</div>
                  )}
                </div>
              </>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
              <button
                onClick={() => setAccountUser(null)}
                disabled={busy}
                style={{ padding: "9px 18px", background: "#fff", color: "#5E2589", border: "1px solid #d5d0dd", borderRadius: "8px", cursor: "pointer" }}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 권한 변경 확인 모달 */}
      {roleConfirm && accountUser && (
        <div style={{ ...modalOverlay, zIndex: 1100 }}>
          <div style={{ ...modalBox, width: "380px" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: "bold", fontSize: "18px", marginBottom: "12px" }}>권한 변경</div>
            <div style={{ color: "#555", lineHeight: 1.6, marginBottom: "20px" }}>
              <b>{accountUser.name}</b> ({accountUser.email}) 을(를){" "}
              <b>{roleConfirm === ROLE_CODE.ADMIN ? "최고 관리자" : roleConfirm === ROLE_CODE.SUB_ADMIN ? "관리자" : "일반유저"}</b> 권한으로 변경할까요?
              {roleConfirm === ROLE_CODE.ADMIN && (
                <div style={{ color: "#d9534f", fontSize: "13px", marginTop: "8px" }}>
                  최고 관리자는 전체 유저·기기·차단·계정·권한을 모두 관리할 수 있습니다.
                </div>
              )}
              {roleConfirm === ROLE_CODE.SUB_ADMIN && (
                <div style={{ color: "#84818A", fontSize: "13px", marginTop: "8px" }}>
                  관리자는 전체 유저 목록만 조회할 수 있습니다. (관리 기능 없음)
                </div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button
                onClick={() => setRoleConfirm(null)}
                disabled={busy}
                style={{ padding: "9px 18px", background: "#fff", color: "#5E2589", border: "1px solid #d5d0dd", borderRadius: "8px", cursor: "pointer" }}
              >
                취소
              </button>
              <button
                onClick={confirmRoleChange}
                disabled={busy}
                style={{ padding: "9px 18px", background: roleConfirm === ROLE_CODE.USER ? "#d9534f" : "#787397", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer" }}
              >
                {busy ? "변경 중..." : "변경"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default withRouter(AdminUserList);
